// src/pages/auth/ResetPasswordPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
  Divider,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { getLangFromCookie } from "../../../Utils/LangCookie";
import {
  wpResetPasswordWithToken,
  wpValidateResetPasswordToken,
} from "../../../api/wpAuth";

/* ------------------------------------------------------------
   Helpers
------------------------------------------------------------ */

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function normalizeLang(lang) {
  const s = String(lang || "").toUpperCase().trim();
  if (s === "EN" || s === "JP" || s === "ZH") return s;
  return "ZH";
}

function pickText(lang) {
  const L = normalizeLang(lang);

  const t = {
    ZH: {
      title: "重設密碼",
      subtitle: "請輸入新密碼。若連結已過期，請回到「忘記密碼」重新申請。",
      tokenMissing: "缺少重設密碼 token。請重新申請忘記密碼。",
      tokenChecking: "正在驗證連結…",
      tokenInvalidTitle: "連結無效",
      tokenInvalidBody:
        "此重設密碼連結已過期或已被使用。請回到「忘記密碼」重新申請。",
      newPassword: "新密碼",
      confirmPassword: "確認新密碼",
      pwMin: "密碼需為 8～20 個字元。",
      pwMismatch: "兩次輸入的密碼不一致。",
      pwRuleFail: "密碼未符合規則，請依下方提示調整。",
      submitting: "送出中…",
      submit: "更新密碼",
      successTitle: "密碼已更新",
      successBody: "請使用新密碼登入。",
      goLogin: "前往登入",
      invalidOrExpired: "連結無效或已過期。請重新申請「忘記密碼」。",

      // password UI
      pwdStrengthLabel: "密碼強度：",
      strengthWeak: "弱",
      strengthMedium: "中",
      strengthStrong: "強",
      pwdRule1: "長度需為 8～20 個字元",
      pwdRule3: "不可與帳號相同",
      pwdRule4: "不可為容易猜測的組合（連號、重複、常見字串等）",
      pwdRule5: "需包含：大小寫英文 + 數字 + 符號（或非英數字元）",
    },
    EN: {
      title: "Reset Password",
      subtitle:
        "Enter your new password. If the link expired, please request a new one from “Forgot password”.",
      tokenMissing: "Missing reset token. Please request a new reset link.",
      tokenChecking: "Validating link…",
      tokenInvalidTitle: "Invalid link",
      tokenInvalidBody:
        "This reset link has expired or has already been used. Please request a new one from “Forgot password”.",
      newPassword: "New password",
      confirmPassword: "Confirm new password",
      pwMin: "Password must be 8–20 characters.",
      pwMismatch: "Passwords do not match.",
      pwRuleFail: "Password rules not satisfied. Please adjust based on the checks below.",
      submitting: "Submitting…",
      submit: "Update password",
      successTitle: "Password updated",
      successBody: "You can now login with your new password.",
      goLogin: "Go to login",
      invalidOrExpired: "Invalid or expired link. Please request a new reset link.",

      // password UI
      pwdStrengthLabel: "Password strength:",
      strengthWeak: "Weak",
      strengthMedium: "Medium",
      strengthStrong: "Strong",
      pwdRule1: "Length must be 8–20 characters",
      pwdRule3: "Must not be the same as username",
      pwdRule4: "Must not be easy to guess (sequences, repeats, common strings, etc.)",
      pwdRule5: "Must include: UPPER + lower letters + number + symbol (or non-ASCII)",
    },
    JP: {
      title: "パスワード再設定",
      subtitle:
        "新しいパスワードを入力してください。リンクが期限切れの場合は「パスワードを忘れた」から再申請してください。",
      tokenMissing: "再設定トークンがありません。もう一度申請してください。",
      tokenChecking: "リンクを確認中…",
      tokenInvalidTitle: "リンクが無効です",
      tokenInvalidBody:
        "この再設定リンクは期限切れ、または既に使用されています。「パスワードを忘れた」から再申請してください。",
      newPassword: "新しいパスワード",
      confirmPassword: "新しいパスワード（確認）",
      pwMin: "パスワードは 8～20 文字にしてください。",
      pwMismatch: "パスワードが一致しません。",
      pwRuleFail: "パスワード規則を満たしていません。下のチェックに沿って調整してください。",
      submitting: "送信中…",
      submit: "パスワードを更新",
      successTitle: "更新しました",
      successBody: "新しいパスワードでログインできます。",
      goLogin: "ログインへ",
      invalidOrExpired: "リンクが無効または期限切れです。再度申請してください。",

      // password UI
      pwdStrengthLabel: "パスワード強度：",
      strengthWeak: "弱い",
      strengthMedium: "普通",
      strengthStrong: "強い",
      pwdRule1: "長さは 8～20 文字",
      pwdRule3: "ユーザー名と同じにしない",
      pwdRule4: "推測されやすい組み合わせは不可（連番・繰り返し・よくある文字列など）",
      pwdRule5: "大文字/小文字 + 数字 + 記号（または非ASCII）を含める",
    },
  };

  return t[L] || t.ZH;
}

/**
 * Accept token from:
 * - ?token=xxxx
 * - ?t=xxxx
 * - /reset-password/:token
 */
function readTokenFromLocation({ query, pathname }) {
  const qToken = query.get("token") || query.get("t");
  if (qToken) return String(qToken).trim();

  const parts = String(pathname || "")
    .split("/")
    .filter(Boolean);
  const idx = parts.findIndex((p) => p === "reset-password" || p === "resetpassword");
  if (idx >= 0 && parts[idx + 1]) return String(parts[idx + 1]).trim();

  return "";
}

/**
 * Listen to language cookie changes without refresh:
 * - poll the cookie value
 * - and also listen to common custom events
 */
function useLiveLangCookie(pollMs = 800) {
  const [lang, setLang] = useState(() => normalizeLang(getLangFromCookie?.() || "ZH"));
  const lastRef = useRef(lang);

  useEffect(() => {
    let alive = true;

    const tick = () => {
      if (!alive) return;
      const next = normalizeLang(getLangFromCookie?.() || "ZH");
      if (next !== lastRef.current) {
        lastRef.current = next;
        setLang(next);
      }
    };

    const id = setInterval(tick, Math.max(300, pollMs));

    const onEvt = () => tick();
    window.addEventListener("app:languageChanged", onEvt);
    window.addEventListener("languageChanged", onEvt);
    window.addEventListener("storage", onEvt);

    tick();

    return () => {
      alive = false;
      clearInterval(id);
      window.removeEventListener("app:languageChanged", onEvt);
      window.removeEventListener("languageChanged", onEvt);
      window.removeEventListener("storage", onEvt);
    };
  }, [pollMs]);

  return lang;
}

/* -------- password rule helpers (same as AuthRegisterForm) -------- */
const initialPasswordRules = {
  length: false,
  notSameAsUsername: false,
  notEasyGuess: false,
  complexity: false,
};

function hasLongNumericSequence(str) {
  const s = str || "";
  if (s.length < 4) return false;

  let ascLen = 1;
  let descLen = 1;

  for (let i = 1; i < s.length; i++) {
    const prev = s[i - 1];
    const curr = s[i];

    if (/[0-9]/.test(prev) && /[0-9]/.test(curr)) {
      const diff = curr.charCodeAt(0) - prev.charCodeAt(0);
      if (diff === 1) {
        ascLen += 1;
        descLen = 1;
      } else if (diff === -1) {
        descLen += 1;
        ascLen = 1;
      } else {
        ascLen = 1;
        descLen = 1;
      }
      if (ascLen >= 4 || descLen >= 4) return true;
    } else {
      ascLen = 1;
      descLen = 1;
    }
  }

  return false;
}

function evaluatePasswordRules(password, opts = {}) {
  const { username = "", phone = "" } = opts;
  const pwd = password || "";
  const uname = (username || "").toLowerCase();
  const phoneDigits = (phone || "").replace(/\D/g, "");

  const length = pwd.length >= 8 && pwd.length <= 20;
  const notSameAsUsername = !pwd || pwd.toLowerCase() !== uname;

  let notEasyGuess = !!pwd;
  if (pwd) {
    const lower = pwd.toLowerCase();
    const common = ["0000", "1111", "qwer", "password"];

    if (uname && uname.length >= 4 && lower.includes(uname)) notEasyGuess = false;

    if (phoneDigits && phoneDigits.length >= 4) {
      const tail4 = phoneDigits.slice(-4);
      if (lower.includes(tail4)) notEasyGuess = false;
    }

    if (common.some((c) => lower.includes(c))) notEasyGuess = false;
    if (hasLongNumericSequence(pwd)) notEasyGuess = false;
    if (/(.)\1\1/.test(pwd)) notEasyGuess = false;
  }

  const hasAsciiLetter = /[A-Za-z]/.test(pwd);
  const hasDigit = /\d/.test(pwd);
  const hasSpecialAscii = /[^A-Za-z0-9]/.test(pwd);
  const hasNonLatin = /[^\u0000-\u007f]/.test(pwd);
  const hasSymbolOrOther = hasSpecialAscii || hasNonLatin;
  const hasUpper = /[A-Z]/.test(pwd);
  const hasLower = /[a-z]/.test(pwd);

  const complexity = !!pwd && hasAsciiLetter && hasDigit && hasSymbolOrOther && hasUpper && hasLower;

  return { length, notSameAsUsername, notEasyGuess, complexity };
}

const PASSWORD_RULE_ITEMS = [
  { key: "length", labelKey: "pwdRule1" },
  { key: "notSameAsUsername", labelKey: "pwdRule3" },
  { key: "notEasyGuess", labelKey: "pwdRule4" },
  { key: "complexity", labelKey: "pwdRule5" },
];

function getPasswordStrength(password, rules = initialPasswordRules, opts = {}) {
  const pwd = password || "";
  if (!pwd) return { level: "none", score: 0 };

  const { username = "" } = opts;
  const lower = pwd.toLowerCase();
  const uname = (username || "").toLowerCase();

  let score = 0;
  if (rules.length) score += 1;
  if (rules.complexity) score += 2;
  if (rules.notEasyGuess) score += 1;
  if (rules.notSameAsUsername) score += 1;
  if (pwd.length >= 12) score += 1;

  const hasAsciiLetter = /[A-Za-z]/.test(pwd);
  const hasDigit = /\d/.test(pwd);
  const hasSpecialAscii = /[^A-Za-z0-9]/.test(pwd);
  const hasNonLatin = /[^\u0000-\u007f]/.test(pwd);
  const hasSymbolOrOther = hasSpecialAscii || hasNonLatin;

  let typeCount = 0;
  [hasAsciiLetter, hasDigit, hasSymbolOrOther].forEach((b) => {
    if (b) typeCount += 1;
  });
  if (typeCount >= 2) score += 1;

  if (!rules.notEasyGuess || !rules.notSameAsUsername || (uname && lower === uname)) {
    return { level: "weak", score };
  }

  let level = "weak";
  if (score >= 5) level = "strong";
  else if (score >= 3) level = "medium";

  return { level, score };
}

/* ------------------------------------------------------------
   Page
------------------------------------------------------------ */

export default function ResetPasswordPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const query = useQuery();

  // ✅ live language (no refresh needed)
  const lang = useLiveLangCookie(800);
  const UI = useMemo(() => pickText(lang), [lang]);

  const token = useMemo(
    () => readTokenFromLocation({ query, pathname: loc.pathname }),
    [query, loc.pathname]
  );

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [show2, setShow2] = useState(false);

  // password rules + strength (same as register form)
  const [passwordRules, setPasswordRules] = useState(initialPasswordRules);
  useEffect(() => {
    // reset page has no username/phone context; keep same evaluator but with empty opts
    setPasswordRules(evaluatePasswordRules(pw, { username: "", phone: "" }));
  }, [pw]);

  const strength = useMemo(() => getPasswordStrength(pw, passwordRules, { username: "" }), [pw, passwordRules]);

  let strengthColor = "grey.400";
  let strengthWidth = "0%";
  if (strength.level === "weak") {
    strengthColor = "error.main";
    strengthWidth = "33%";
  } else if (strength.level === "medium") {
    strengthColor = "warning.main";
    strengthWidth = "66%";
  } else if (strength.level === "strong") {
    strengthColor = "success.main";
    strengthWidth = "100%";
  }

  const allRulesPassed = useMemo(() => {
    return (
      !!pw &&
      passwordRules.length &&
      passwordRules.notSameAsUsername &&
      passwordRules.notEasyGuess &&
      passwordRules.complexity
    );
  }, [pw, passwordRules]);

  // submit status
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  // ✅ token validation status
  // "idle" | "checking" | "valid" | "invalid" | "missing"
  const [tokenStatus, setTokenStatus] = useState("idle");
  const [tokenInvalidMsg, setTokenInvalidMsg] = useState("");

  // ✅ Validate token first (invalid => render message; valid => allow reset)
  useEffect(() => {
    let alive = true;

    async function run() {
      setOk(false);
      setLoading(false);
      setErr("");
      setTokenInvalidMsg("");

      if (!token) {
        setTokenStatus("missing");
        return;
      }

      setTokenStatus("checking");
      try {
        const res = await wpValidateResetPasswordToken({ token }, { debug: false });

        if (!alive) return;

        if (res?.ok) {
          setTokenStatus("valid");
          return;
        }

        setTokenStatus("invalid");
        setTokenInvalidMsg(String(res?.message || ""));
      } catch (e) {
        if (!alive) return;
        setTokenStatus("invalid");
        setTokenInvalidMsg(String(e?.message || ""));
      }
    }

    run();

    return () => {
      alive = false;
    };
  }, [token]);

  // If token missing, show message (keeps in sync with lang changes)
  useEffect(() => {
    if (tokenStatus === "missing") setErr(UI.tokenMissing);
    else if (tokenStatus !== "missing") {
      if (err === UI.tokenMissing) setErr("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenStatus, UI.tokenMissing, lang]);

  const canSubmit = useMemo(() => {
    if (!token) return false;
    if (tokenStatus !== "valid") return false;
    if (loading) return false;
    if (!pw || !pw2) return false;
    if (pw !== pw2) return false;
    if (!allRulesPassed) return false; // ✅ same rule gating style as register UI
    return true;
  }, [token, tokenStatus, loading, pw, pw2, allRulesPassed]);

  async function onSubmit(e) {
    e?.preventDefault?.();
    setErr("");

    if (!token) {
      setErr(UI.tokenMissing);
      return;
    }
    if (tokenStatus !== "valid") {
      setTokenStatus("invalid");
      return;
    }
    if (pw !== pw2) {
      setErr(UI.pwMismatch);
      return;
    }
    if (!passwordRules.length) {
      setErr(UI.pwMin);
      return;
    }
    if (!allRulesPassed) {
      setErr(UI.pwRuleFail);
      return;
    }

    setLoading(true);
    try {
      const res = await wpResetPasswordWithToken({ token, newPassword: pw }, { debug: false });

      if (!res?.ok) {
        const msg = String(res?.message || UI.invalidOrExpired);

        const msgLower = msg.toLowerCase();
        const looksInvalid =
          msgLower.includes("expired") ||
          msgLower.includes("invalid") ||
          msgLower.includes("used") ||
          msgLower.includes("token");

        if (looksInvalid) {
          setTokenStatus("invalid");
          setTokenInvalidMsg(msg);
        } else {
          setErr(msg);
        }

        setLoading(false);
        return;
      }

      setOk(true);
      setLoading(false);
    } catch (e2) {
      const msg = String(e2?.message || UI.invalidOrExpired);

      const msgLower = msg.toLowerCase();
      const looksInvalid =
        msgLower.includes("expired") ||
        msgLower.includes("invalid") ||
        msgLower.includes("used") ||
        msgLower.includes("token");

      if (looksInvalid) {
        setTokenStatus("invalid");
        setTokenInvalidMsg(msg);
      } else {
        setErr(msg);
      }

      setLoading(false);
    }
  }

  const showTokenInvalid = tokenStatus === "invalid";
  const showChecking = tokenStatus === "checking";
  const showForm = !ok && tokenStatus === "valid";

  const strengthLabel =
    strength.level === "strong"
      ? UI.strengthStrong
      : strength.level === "medium"
      ? UI.strengthMedium
      : strength.level === "weak"
      ? UI.strengthWeak
      : "-";

  return (
    <Box sx={{ py: { xs: 4, md: 8 } }}>
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3 }}>
          <Stack spacing={2.2}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                {UI.title}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.8 }}>
                {UI.subtitle}
              </Typography>
            </Box>

            {/* Checking */}
            {showChecking ? (
              <Alert severity="info" icon={false}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2">{UI.tokenChecking}</Typography>
                </Stack>
              </Alert>
            ) : null}

            {/* Token invalid / used / expired */}
            {showTokenInvalid ? (
              <Alert severity="error">
                <Typography sx={{ fontWeight: 800 }}>{UI.tokenInvalidTitle}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {UI.tokenInvalidBody}
                </Typography>

                {tokenInvalidMsg ? (
                  <Typography variant="caption" sx={{ display: "block", mt: 1, opacity: 0.7 }}>
                    {tokenInvalidMsg}
                  </Typography>
                ) : null}

                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    size="large"
                    sx={{ py: 1.1, borderRadius: 999 }}
                    onClick={() => nav("/auth")}
                  >
                    {UI.goLogin}
                  </Button>
                </Box>
              </Alert>
            ) : null}

            {/* Success */}
            {ok ? (
              <Alert severity="success">
                <Typography sx={{ fontWeight: 700 }}>{UI.successTitle}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {UI.successBody}
                </Typography>
              </Alert>
            ) : null}

            {/* Other errors */}
            {err ? <Alert severity="error">{err}</Alert> : null}

            {/* Form only when token is VALID */}
            {showForm ? (
              <Box component="form" onSubmit={onSubmit}>
                <Stack spacing={2}>
                  <TextField
                    label={UI.newPassword}
                    type={show ? "text" : "password"}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    fullWidth
                    autoComplete="new-password"
                    disabled={loading}
                    inputProps={{ spellCheck: "false", autoCapitalize: "none", autoCorrect: "off" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShow((v) => !v)} edge="end" disabled={loading}>
                            {show ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* strength + rules (same UI style as AuthRegisterForm) */}
                  {pw ? (
                    <Box sx={{ mt: -0.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                          {UI.pwdStrengthLabel}
                        </Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: strengthColor }}>
                          {strengthLabel}
                        </Typography>
                      </Stack>

                      <Box sx={{ height: 8, borderRadius: 999, bgcolor: "grey.200", overflow: "hidden", mb: 0.75 }}>
                        <Box
                          sx={{
                            height: "100%",
                            width: strengthWidth,
                            transition: "width 0.25s ease",
                            bgcolor: strengthColor,
                          }}
                        />
                      </Box>

                      {PASSWORD_RULE_ITEMS.map((item) => {
                        const passed = passwordRules[item.key];
                        const Icon = passed ? CheckCircleRoundedIcon : CancelRoundedIcon;
                        return (
                          <Stack key={item.key} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                            <Icon sx={{ fontSize: 18, color: passed ? "success.main" : "error.main" }} />
                            <Typography
                              variant="caption"
                              sx={{ color: passed ? "success.main" : "error.main", fontSize: 12 }}
                            >
                              {UI[item.labelKey] || item.labelKey}
                            </Typography>
                          </Stack>
                        );
                      })}

                      <Divider sx={{ mt: 1.25 }} />
                    </Box>
                  ) : null}

                  <TextField
                    label={UI.confirmPassword}
                    type={show2 ? "text" : "password"}
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    fullWidth
                    autoComplete="new-password"
                    disabled={loading}
                    inputProps={{ spellCheck: "false", autoCapitalize: "none", autoCorrect: "off" }}
                    error={!!pw2 && pw !== pw2}
                    helperText={!!pw2 && pw !== pw2 ? UI.pwMismatch : " "}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShow2((v) => !v)} edge="end" disabled={loading}>
                            {show2 ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={!canSubmit}
                    sx={{ py: 1.25, borderRadius: 999 }}
                    startIcon={loading ? <CircularProgress size={18} /> : undefined}
                  >
                    {loading ? UI.submitting : UI.submit}
                  </Button>
                </Stack>
              </Box>
            ) : null}

            {/* After success: go login */}
            {ok ? (
              <Button
                variant="contained"
                size="large"
                sx={{ py: 1.25, borderRadius: 999 }}
                onClick={() => nav("/auth")}
              >
                {UI.goLogin}
              </Button>
            ) : null}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
